import { useContext, useMemo, useState } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import { InputText, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { isEmpty as _isEmpty } from "lodash"
import moment from "moment"

import { LineGraph } from "../../../components/graphs/Line/LineGraph"
import { KonanAPI } from "../../../services/KonanAPI"
import type { RootState } from "../../../store/ReduxStore"
import { Deployment } from "../../../types/generated/api/Deployment"
import { InsightList } from "../../../types/generated/api/InsightList"
import { ReportContext } from "../Context"
import type { InsightProps, LabelTimeInsightProps } from "../interfaces"

/**
 * LabelTimeCountInsight Component
 *
 * Visualizes the distribution of label counts over time for selected workflows, labels, and reasons.
 * It fetches data from the API and displays it using a line graph.
 *
 * @param {Readonly<InsightProps>} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Grid item containing the LineGraph visualization
 */
export function LabelTimeCountInsight(props: Readonly<InsightProps>): React.ReactElement {
  const { insight } = props
  const { id: projectId } = useParams<{ id: string }>()

  const { report } = useContext(ReportContext)

  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  const { t } = useTranslation("insights")

  const ALL_ENGINES = currentProject?.category === Deployment.category.MODEL ? "__all_models__" : "__all_workflows__"
  const ENGINE =
    currentProject?.category === Deployment.category.MODEL ? t("engineTypes.models") : t("engineTypes.workflows")

  // State variables to manage selected workflows, labels, and reasons
  const [selectedEngines, setSelectedEngines] = useState<string[] | undefined>(undefined)
  const [selectedLabels, setSelectedLabels] = useState<string[] | undefined>(undefined)
  const [selectedReasons, setSelectedReasons] = useState<string[] | undefined>(undefined)

  // Fetch labels insight data from the API
  const { data: insightData, isLoading: isLoadingInsightData } = useQuery<
    AxiosResponse<LabelTimeInsightProps>,
    AxiosError
  >(
    ["label-time-count", projectId, report?.uuid, insight.uuid],
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      // Enable the query only when required parameters are available
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const isEmpty = _isEmpty(insightData?.data?.data) // Check if the data is empty

  // TODO:: refactor to use getTickInterval
  const getPrecision = useMemo((): "hour" | "day" | "month" | "year" | undefined => {
    const diff = moment(report?.predictions_end_time).diff(report?.predictions_start_time, "d")

    if (diff <= 3) {
      return "hour"
    } else if (diff <= 366) {
      return "day"
    } else if (diff <= 3653) {
      return "month"
    } else {
      return "year"
    }
  }, [report?.predictions_end_time, report?.predictions_start_time])

  // Memoized function to get chart metadata (workflows/models (engines), labels, reasons)
  const getChartMetaData = useMemo((): { engines: string[]; labels: string[]; reasons: string[] } | undefined => {
    if (isLoadingInsightData || !insightData?.data?.data || _isEmpty(insightData?.data?.data)) return undefined
    else {
      const engines = Object.keys(insightData?.data?.data).filter((engine) => engine !== ALL_ENGINES)

      const labels = Object.keys(insightData?.data?.data?.[engines[0] as string] ?? {})
      const reasons = Object.keys(insightData?.data?.data?.[engines[0] as string]?.[labels[0] as string]?.reasons ?? {})

      return { engines, labels, reasons } // Return metadata
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insightData?.data?.data, isLoadingInsightData])

  // Memoized function to get chart data based on selected options
  const getChartData = useMemo(() => {
    if (isLoadingInsightData || !insightData?.data?.data || _isEmpty(insightData?.data?.data)) return undefined
    else {
      const metaData = getChartMetaData
      const data = []

      // Loop through selected workflows and labels to build data for the chart
      for (const workflow of selectedEngines ?? [ALL_ENGINES]) {
        for (const label of selectedLabels ?? metaData?.labels ?? []) {
          if (selectedReasons && selectedReasons.length !== 0) {
            for (const reason of selectedReasons)
              if ((insightData?.data?.data?.[workflow]?.[label]?.reasons?.[reason] ?? []).length > 0) {
                const items = insightData?.data?.data?.[workflow]?.[label]?.reasons?.[reason]

                items &&
                  items.length > 0 &&
                  data.push({
                    id: `${
                      workflow === ALL_ENGINES
                        ? t("LabelTimeCount.filters.engine.all", { engineType: ENGINE })
                        : workflow
                    } -> ${label} -> ${reason}`,
                    data: [
                      {
                        x: moment(items?.[0]?.start_datetime).toDate(),
                        y: 0,
                      },
                      ...(items?.map((item) => {
                        return { x: moment(item.end_datetime).toDate(), y: item.count }
                      }) ?? []),
                    ],
                  })
              }
          } else {
            const items = insightData?.data?.data?.[workflow]?.[label]?.["__all_reasons__"]

            items &&
              items.length > 0 &&
              data.push({
                id: `${workflow === ALL_ENGINES ? t("LabelTimeCount.filters.engine.all", { engineType: ENGINE }) : workflow} -> ${label} -> ${t("LabelTimeCount.filters.reasons.all")}`,
                data: [
                  {
                    x: moment(items[0]?.start_datetime).toDate(),
                    y: 0,
                  },
                  ...items.map((item) => {
                    return { x: moment(item.end_datetime).toDate(), y: item.count }
                  }),
                ],
              })
          }
        }
      }

      return data // Return the constructed data
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoadingInsightData,
    insightData?.data?.data,
    getChartMetaData,
    selectedEngines,
    selectedLabels,
    selectedReasons,
  ])

  // Handler for selecting engines
  const handleEngineSelect = (selectedOptions: string[]): void => {
    if (selectedOptions.length === 0) {
      setSelectedEngines(undefined) // Reset if no options are selected
      return
    }
    setSelectedEngines(selectedOptions) // Update selected engines
  }

  // Handler for selecting reasons
  const handleReasonSelect = (selectedOptions: string[]): void => {
    if (selectedOptions.length === 0) {
      setSelectedReasons(undefined) // Reset if no options are selected
      return
    }
    setSelectedReasons(selectedOptions) // Update selected reasons
  }

  // Handler for selecting labels
  const handleLabelSelect = (selectedOptions: string[]): void => {
    if (selectedOptions.length === 0) {
      setSelectedLabels(undefined) // Reset if no options are selected
      return
    }
    setSelectedLabels(selectedOptions) // Update selected labels
  }

  // Subheader component for the LineGraph
  const subHeader = (): React.ReactElement => {
    return (
      <Grid container columnSpacing={1}>
        <Grid item xs={4}>
          <InputText
            options={getChartMetaData?.engines ?? []}
            value={
              selectedEngines === undefined
                ? t("LabelTimeCount.filters.engine.all", { engineType: ENGINE })
                : selectedEngines?.length > 0
                  ? t("LabelTimeCount.filters.engine.selected", { engineType: ENGINE, count: selectedEngines?.length })
                  : t("LabelTimeCount.filters.engine.select", { engineType: ENGINE })
            }
            multipleModeProps={{
              isActive: true,
              selectedValues: selectedEngines ?? [],
              setSelectedValues: (selectedOptions) => handleEngineSelect(selectedOptions as string[]),
              allowDeselect: (selectedEngines?.length ?? 0) > 0,
              hideChips: true,
            }}
            isLoadingOptions={isLoadingInsightData}
            isSelect
            fullWidth
            hideDescription
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>

        <Grid item xs={4}>
          <InputText
            options={getChartMetaData?.labels ?? []}
            value={
              selectedLabels === undefined
                ? t("LabelTimeCount.filters.labels.all")
                : selectedLabels?.length && selectedLabels?.length > 0
                  ? t("LabelTimeCount.filters.labels.selected", { count: selectedLabels?.length })
                  : t("LabelTimeCount.filters.labels.select")
            }
            isLoadingOptions={isLoadingInsightData}
            multipleModeProps={{
              isActive: true,
              selectedValues: selectedLabels ?? [],
              setSelectedValues: (selectedOptions) => handleLabelSelect(selectedOptions as string[]),
              allowDeselect: (selectedLabels?.length ?? 0) > 0,
              hideChips: true,
            }}
            isSelect
            fullWidth
            hideDescription
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>

        <Grid item xs={4}>
          <InputText
            options={getChartMetaData?.reasons ?? []}
            value={
              selectedReasons === undefined
                ? t("LabelTimeCount.filters.reasons.all")
                : selectedReasons?.length && selectedReasons?.length > 0
                  ? t("LabelTimeCount.filters.reasons.selected", { count: selectedReasons?.length })
                  : t("LabelTimeCount.filters.reasons.select")
            }
            multipleModeProps={{
              isActive: true,
              selectedValues: selectedReasons ?? [],
              setSelectedValues: (selectedOptions) => handleReasonSelect(selectedOptions as string[]),
              allowDeselect: (selectedReasons?.length ?? 0) > 0,
              hideChips: true,
            }}
            isLoadingOptions={isLoadingInsightData}
            isSelect
            fullWidth
            hideDescription
            menuProps={{ menuMaxContent: true }}
          />
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid item xs={12}>
      <LineGraph
        graphHeight={340}
        title={t("LabelTimeCount.title")}
        tooltipText={<Trans>{t("LabelTimeCount.tooltip", { engineType: ENGINE })}</Trans>}
        data={getChartData ?? []}
        timeScalePrecision={getPrecision}
        isLoading={isLoadingInsightData}
        subHeader={subHeader()}
        variant="time"
        tooltipComponent={({ slice }) => (
          <div className={"chart-tooltip"} style={{ width: "fit-content" }}>
            <Typography variant="h2-bold" gutterBottom>
              {moment(slice.points[0]?.data.x)
                .format(getPrecision === "hour" ? `LLL` : "LL")
                .toString()}
            </Typography>

            {slice.points.map((entry) => (
              <Typography variant="p" variantColor={2} key={entry.color} gutterBottom>
                {entry.serieId}: <strong>{entry.data.y as string}</strong>
              </Typography>
            ))}
          </div>
        )}
        emptyState={
          insight.status !== InsightList.status.SUCCEEDED
            ? {
                title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`,
              }
            : isEmpty
              ? {
                  title: t("LabelTimeCount.unavailable"),
                }
              : undefined
        }
      />
    </Grid>
  )
}
